import { cloneDeep } from "lodash";
import moment from "moment";
import {
    allBudgetTypesOptions,
    allClassificationTypesOptions,
    allDataSourcesOptions,
    allDevelopTypesOptions,
    allExpensesClassificators,
    allMeasureUnitsOptions,
    allMutuallyRedeemingLevelOptions,
    allMutuallyRedeemingOptions,
    allPeriodicitiesOptions,
    allReportNamesOptions,
    allReportTypesOptions,
    allSegmentationOptions,
    allStatusOptions
} from "../../common/baseOptions";
import { Template } from "../../common/types/Template";
import { ReportSvodRasFilterSettings } from "./settings/ReportSvodRasFilterSettings";
import { ReportSvodRasFilterOptions } from "./options/ReportSvodRasFilterOptions";
import { Indicator } from "../../common/types/Indicator";
import { Option } from "../../common/types/Option";
import { ByForm, DataSource, StatusType } from "../../common/types/roots/fields";

/**
 * Показатели отчета «Сводный отчет об исполнении расходов бюджета» (EAK-453).
 * Порядок строго фиксирован (шаблон, столбцы H-T): три годовых блока
 * (Y-2, Y-1, Y) + две расчетные колонки отклонений.
 */
export const reportSvodRasIndicators: Indicator[] = [
    { key: 'plan_year_y2', active: true },
    { key: 'fact_year_end_y2', active: true },
    { key: 'plan_date_y2', active: true },
    { key: 'fact_date_y2', active: true },
    { key: 'plan_year_y1', active: true },
    { key: 'fact_year_end_y1', active: true },
    { key: 'plan_date_y1', active: true },
    { key: 'fact_date_y1', active: true },
    { key: 'dev_fact_y1_y2', active: true },
    { key: 'plan_year', active: true },
    { key: 'plan_date', active: true },
    { key: 'fact_date', active: true },
    { key: 'dev_fact_y_y1', active: true },
    // Дополнительные опциональные показатели (Критерии приёмки ТЗ) — не входят в дефолтный
    // шаблон H-T, скрыты по умолчанию, включаются через «Скрыть показатели»/drag-n-drop.
    { key: 'pct_plan_date', active: false },
    { key: 'dev_plan_date', active: false },
    { key: 'pct_plan_year', active: false },
    { key: 'dev_plan_year', active: false },
    { key: 'growth_pct', active: false },
]

export const svodRasByFormOptions: Option<ByForm>[] = [
    { value: '4-20', label: '4-20', labelKk: '4-20', labelEn: '4-20' },
]

export const SvodRasStatusOptions: Option<StatusType>[] = allStatusOptions.filter((v: Option<StatusType>) => ['39', '26', '0'].includes(v.value))

// Нужна как временная затычка (при создании нового шаблона берутся пустые настройки)
const tempSettings: ReportSvodRasFilterSettings = {
    // Base:
    reportName: 'SVOD-RAS',
    reportType: 'REGULATED',
    classificationType: 'EXPENSES',
    dataSource: 'IISK',
    // Отчет строится на одну отчетную дату (поле date), поэтому периодичность - дневная.
    // Пара «periodicity + поле даты» разбирается в setRegionOptionsOnCreate (filters-form2.vue):
    // при 'MONTH' там ожидается поле month, которого у отчета нет, и список регионов остается пустым.
    periodicity: 'DAY',
    // Custom:
    dataSource2: 'E-AKIMAT',
    date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
    region: '',
    budgetTypes: [],
    segmentations: [],
    expenses: cloneDeep(allExpensesClassificators),
    mutuallyRedeeming: 'WITH',
    mutuallyRedeemingLevels: [],
    statuses: ['39'],
    measureUnit: 'THOUSAND',
    roundUpTo: null,
    comparePrevYear: true,
    regionsTotal: false,
    isTree: true,
    fillCbc: false,
    kbkThisYearOnly: true,
    developType: null,
    totalFinSource: false,
    byForm: '4-20',
    indicators: cloneDeep(reportSvodRasIndicators)
}

export const reportSvodRasBaseTemplate: Template = {
    id: null,
    name: 'Сводный отчет об исполнении расходов бюджета',
    excelName: null,
    description: null,
    category: '50',
    regions: [],
    userId: '',
    filterSettings: tempSettings
}

let dataSources2: Option<DataSource>[] = [{ value: 'E-AKIMAT', label: 'eAkimat', labelKk: 'eAkimat', labelEn: 'eAkimat' }]
const IISK: Option<DataSource>[] = cloneDeep(allDataSourcesOptions.filter(it => it.value === 'IISK'))
dataSources2 = dataSources2.concat(IISK)

export const reportSvodRasFilterOptions: ReportSvodRasFilterOptions = {
    // Base:
    key: 'SVOD-RAS',
    reportNames: cloneDeep(allReportNamesOptions),
    reportTypes: cloneDeep(allReportTypesOptions),
    // Отчет только расходный - классификация зафиксирована и недоступна для изменения в UI
    classificationTypes: cloneDeep(allClassificationTypesOptions.filter(it => it.value === 'EXPENSES')),
    dataSources: cloneDeep(allDataSourcesOptions.filter(it => it.value !== 'MIXED')),
    periodicities: cloneDeep(allPeriodicitiesOptions),
    // Custom:
    dataSources2: dataSources2,
    measureUnits: cloneDeep(allMeasureUnitsOptions),
    budgetTypes: cloneDeep(allBudgetTypesOptions).filter(it => it.value !== 1),
    regions: [], // Загружается с бэка
    segmentations: cloneDeep(allSegmentationOptions),
    expenses: cloneDeep(allExpensesClassificators),
    mutuallyRedeeming: cloneDeep(allMutuallyRedeemingOptions),
    mutuallyRedeemingLevels: cloneDeep(allMutuallyRedeemingLevelOptions),
    statuses: cloneDeep(SvodRasStatusOptions),
    byForms: cloneDeep(svodRasByFormOptions),
    indicators: cloneDeep(reportSvodRasIndicators),
    developTypes: cloneDeep(allDevelopTypesOptions),
}
